import * as Yup from 'yup'
import {
  DATE_FORMAT_API,
  validateDateFormatApi
} from '../../utility/formatters/DateFormatter'
import { type CellOwnerAgreementTemplateVersionDetails } from '../asset-holdings/CellOwnerAgreementDomainDtos'
import { type UserCrudPermissions } from '../authorisation/UserPermissionsDto'
import { type CurrencyDetails } from '../financial/CurrencyDomainDtos'
import { type LegacySolarConsumerDetails, type LegacySolarInstallerDetails } from '../party/LegacyOrganisationDomainDtos'
import { type CharityOrganisationDetails } from '../party/OrganisationDomainDtos'
import { type CountryDetails } from '../reference/CountryDomainDtos'

// Move this somewhere else when we share it
interface EnumListItem {
  code: string
  description: string
}

export interface LedgerMutationsByCurrencySummary {
  id: number
  currencyId: number
  currency: string
  currencyCode: string
  amount: number
  srcCurrencyId: number
  srcCurrency: string
  srcCurrencyCode: string
  srcAmount: number
}

// ===================================================================================================================
// + sync from back-end-v2/src/domains/asset/api-schema-dtos/AssetProjectCrudDtos.ts
export interface AssetProjectIdentifier {
  projectUrlSlug: string
  projectName: string
}

export interface AssetProjectCreateDto {
  identifier: {
    name: string
    displayName: string
    urlSlug: string
    projectCode: string
    hubspotDealId: string
  }
}

export const validateAssetProjectCreateDto = (): any => {
  return Yup.object().shape({
    identifier: Yup.object({
      name: Yup.string().required(),
      displayName: Yup.string().required(),
      urlSlug: Yup.string().required(),
      projectCode: Yup.string().required(),
      hubspotDealId: Yup.string().required()
    })
  })
}

export interface AssetProjectUpdateDto {
  projectId: number
  solarProjectId: number

  urlSlug: string
  name: string
  projectStatus: string
  projectCrowdsaleStatus: string | null

  userPermissions: UserCrudPermissions
  overrideLockRequested: boolean

  lookups: {
    enabledCurrencies: CurrencyDetails[]
    projectBillingTypes: EnumListItem[]
    projectStatusTypes: EnumListItem[]
    coaTemplateVersions: CellOwnerAgreementTemplateVersionDetails[]
    charities: CharityOrganisationDetails[]
    solarInstallers: LegacySolarInstallerDetails[]
    solarConsumers: LegacySolarConsumerDetails[]
    countries: CountryDetails[]
    marketingSectors: string[]
  }

  identifier: {
    isLocked: boolean
    name: string
    displayName: string
    urlSlug: string
    projectCode: string
    hubspotDealId: string
  }

  status: {
    isLocked: boolean
    projectStatus: string
    published: boolean
    statusMessage?: string | null
  }

  dealModel: {
    isLocked: boolean
    projectCurrencyCode: string
    projectCurrencyId: number
    numberOfCells: number | null
    cellCost: number | null
    electricityCost: number | null
    leaseTermYears: number | null
    estimatedCellRental: number | null
    estimatedIRR: number | null
    estimatedIRRRange: string | null
    useIRRRange: boolean
  }

  solarMetadata: {
    isLocked: boolean
    kwpOutput: number | null
    annualCellProductionKwh: number | null
    legacyProjectForecast: string // JSON => LegacyProjectForecast[]
    batteryIncluded: boolean
    batteryInverterCapacity: number | null
    batteryNameplateCapacityKwh: number | null
    batteryDischargeCapacityKwh: number | null
  }

  billing: {
    isLocked: boolean
    billingCurrencyCode: string
    billingCurrencyId: number
    managementFee: number | null
    projectBillingType: string
    preDistributionDeductions: number | null
  }

  website: {
    isLocked: boolean
    investmentReturnGraphData: string
    carbonOffsetRate: number | null
    marketingSector: string | null
    description: string | null
    summary: string | null
    videoLink: string | null
    brochureUrl: string | null
    mainImageUrl: string | null
    featured: boolean
  }

  organisations: {
    isLocked: boolean
    charityMemberId: number | null
    // charityOrganisationId: number | null
    solarConsumerId: number | null
    solarInstallerId: number | null
  }

  location: {
    isLocked: boolean
    addressLine1: string | null
    addressLine2: string | null
    city: string | null
    stateOrProvince: string | null
    postalCode: string | null
    countryName: string | null
    countryId: number | null
    embeddedMapUrl: string | null
  }

  cellOwnerAgreements: {
    isLocked: boolean
    assetDescription: string
    fixedFirstYearElectricityCost: number | undefined
    adjustmentBasisPercentage: string
    buyoutSchedule: string
    templateVersionId: number | null
  }

  cellOwnerAgreementsStatus: {
    countCellOwners: number
    countOrders: number
    countCurrentCellOwnerAgreements: number
    countCellOwnerAgreementPdfs: number
    templateVersion: string
    enableGenerateAgreements: boolean
    enableGenerateAgreementPdfs: boolean
  }

  lifecycleDates: {
    isLocked: boolean
    campaignEnds: string | null
    crowdsaleStartDate: string | null
    crowdsaleEndDate: string | null
    cellOwnerAgreementEffectiveDate: string | null
    earlyEnergyDate: string | null
    supplierCommercialOperationDate: string | null
    assetLeaseCommercialOperationDate: string | null
  }

  crowdSale: {
    countProjectCells: number | null
    countCellOwners: number | null
    countOrders: number | null
    countCurrentCellOwnerAgreements: number | null
    countCellsInOrders: number | null
    totalSrcAmountExpected: number | null
    totalSrcAmountAccumulated: number | null
    ordersSummaries: LedgerMutationsByCurrencySummary[] | null
    totalFreeCells: number | null
  }
}

export const validateAssetProjectUpdateDto = (): any => {
  return Yup.object().shape({
    projectId: Yup.number().required(),
    urlSlug: Yup.string().required(),

    name: Yup.string().required(),
    projectStatus: Yup.string().required(),
    projectCrowdsaleStatus: Yup.string().nullable(true),

    identifier: Yup.object({
      isLocked: Yup.boolean(),
      name: Yup.string().required(),
      displayName: Yup.string().required(),
      projectCode: Yup.string().required(),
      hubspotDealId: Yup.string().required()
    }),

    status: Yup.object({
      isLocked: Yup.boolean(),
      projectStatus: Yup.string().required(),
      published: Yup.boolean().required(),
      statusMessage: Yup.string().max(2000).nullable(true)
    }),

    dealModel: Yup.object({
      isLocked: Yup.boolean(),
      projectCurrencyId: Yup.number().integer(),
      numberOfCells: Yup.number().min(0).integer().nullable(true),
      cellCost: Yup.number().min(0).integer().nullable(true),
      leaseTermYears: Yup.number().min(0).integer().nullable(true),
      estimatedCellRental: Yup.number().min(0).nullable(true),
      electricityCost: Yup.number().min(0).nullable(true),
      estimatedIRR: Yup.number().min(0).nullable(true),
      estimatedIRRRange: Yup.string().nullable(true),
      useIRRRange: Yup.boolean()
    }),

    solarMetadata: Yup.object({
      isLocked: Yup.boolean(),
      kwpOutput: Yup.number().min(0).nullable(true),
      annualCellProductionKwh: Yup.number().min(0).nullable(true),
      legacyProjectForecast: Yup.string().min(2).required(), // 2 = []
      batteryIncluded: Yup.boolean(),
      batteryInverterCapacity: Yup.number().min(0).nullable(true),
      batteryNameplateCapacityKwh: Yup.number().min(0).nullable(true),
      batteryDischargeCapacityKwh: Yup.number().min(0).nullable(true)
    }),

    billing: Yup.object({
      isLocked: Yup.boolean(),
      billingCurrencyCode: Yup.string().nullable(true),
      managementFee: Yup.number().positive().min(0).max(1).nullable(true),
      preDistributionDeductions: Yup.number().nullable(true)
    }),

    website: Yup.object({
      charityMemberId: Yup.number().nullable(true),
      // charityOrganisationId: Yup.number().positive().nullable(true),
      investmentReturnGraphData: Yup.string().min(2).required(),
      carbonOffsetRate: Yup.number().nullable(true),
      marketingSector: Yup.string().nullable(true)
    }),

    location: Yup.object({
      addressLine1: Yup.string().nullable(true),
      addressLine2: Yup.string().nullable(true),
      city: Yup.string().nullable(true),
      stateOrProvince: Yup.string().nullable(true),
      postalCode: Yup.string().nullable(true),
      countryName: Yup.string().nullable(true),
      countryCode: Yup.string().nullable(true),
      embeddedMapUrl: Yup.string().nullable(true)
    }),

    cellOwnerAgreements: Yup.object({
      isLocked: Yup.boolean(),
      assetDescription: Yup.string().min(5),
      fixedFirstYearElectricityCost: Yup.number().positive().nullable(true),
      adjustmentBasisPercentage: Yup.string().nullable(true),
      buyoutSchedule: Yup.string().min(2).required(), // 2 = []
      templateVersionId: Yup.number().positive().nullable(true)
    }),

    lifecycleDates: Yup.object({
      isLocked: Yup.boolean(),

      campaignEnds: Yup.string().nullable(true)
        .test('test-date-format', `Invalid date format, should be ${DATE_FORMAT_API}`,
          (value) => validateDateFormatApi(value)
        ),
      crowdsaleStartDate: Yup.string().nullable(true)
        .test('test-date-format', `Invalid date format, should be ${DATE_FORMAT_API}`,
          (value) => validateDateFormatApi(value)
        ),
      crowdsaleEndDate: Yup.string().nullable(true)
        .test('test-date-format', `Invalid date format, should be ${DATE_FORMAT_API}`,
          (value) => validateDateFormatApi(value)
        ),
      cellOwnerAgreementEffectiveDate: Yup.string().nullable(true)
        .test('test-date-format', `Invalid date format, should be ${DATE_FORMAT_API}`,
          (value) => validateDateFormatApi(value)
        ),
      earlyEnergyDate: Yup.string().nullable(true)
        .test('test-date-format', `Invalid date format, should be ${DATE_FORMAT_API}`,
          (value) => validateDateFormatApi(value)
        ),
      supplierCommercialOperationDate: Yup.string().nullable(true)
        .test('test-date-format', `Invalid date format, should be ${DATE_FORMAT_API}`,
          (value) => validateDateFormatApi(value)
        ),
      assetLeaseCommercialOperationDate: Yup.string().nullable(true)
        .test('test-date-format', `Invalid date format, should be ${DATE_FORMAT_API}`,
          (value) => validateDateFormatApi(value)
        )
    }),

    cms: Yup.object({
      description: Yup.string().nullable(true),
      summary: Yup.string().nullable(true),
      videoLink: Yup.string().nullable(true),
      brochureUrl: Yup.string().nullable(true),
      mainImageUrl: Yup.string().nullable(true),
      featured: Yup.boolean().nullable(true)
    })
  })
}
